import { Box, Dialog, DialogContent, Grid } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AppointmentRequestCards from './AppointmentRequestCards';
import AppointmentRequestFilters from './AppointmentRequestFilters';
import AppointmentRequestHeader from './AppointmentRequestHeader';
import AppointmentRequestTable from './AppointmentRequestTable';
import AppointmentRequestTabs from './AppointmentRequestTabs';

import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { APPOINTMENT_STATUS } from '../../../config/dataService/constants';
import { appointmentDetails } from '../../../redux/appointment/action';
import {
    appointmentKeys,
    useAppointmentDetails,
    useAppointmentRequests,
    useAppointmentRequestsCard,
} from '../../../services/business/appointment/appointment.hooks';
import useGlobalStore from '../../../store/useGlobalStore';
import AppointmentRequestModal from '../../modal/AppointmentrequestModal';
import AppointmentModel from '../AppointmentModel';
import ViewUpcomingModal from '../UpcomingAppointment/ViewUpcomingModal';

// ── Tab index → API status ─────────────────────────────────────────────────
const TAB_STATUS_MAP = {
    0: 'outstanding',
    1: 'accepted',
    2: 'proposed_new_time',
    3: 'declined',
    4: 'expired',
};

// ── URL route segment → tab index ─────────────────────────────────────────
const ROUTE_TAB_MAP = {
    outstanding: 0,
    accepted: 1,
    proposed: 2,
    declined: 3,
    expired: 4,
};

const TAB_ROUTES = ['/requests/outstanding', '/requests/accepted', '/requests/proposed', '/requests/declined', '/requests/expired'];

/** `requestStatus` values on list/detail payload for accepted-tab cancellation banner */
const REQUEST_CANCEL_STATUS = {
    SHOP: 'cancelled_by_shop_owner',
    CUSTOMER: 'cancelled_by_customer',
};

/** `requestStatus` values on list/detail payload for accepted-tab cancellation banner */
const REQUEST_DECLINE_STATUS = {
    SHOP: 'decline_by_shop_owner',
    CUSTOMER: 'decline_by_customer',
};

// ── Debounce helper ────────────────────────────────────────────────────────
function debounce(func, wait) {
    let timeout;
    return function (...args) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
    pageBox: { bgcolor: 'background.main' },
    fullWidth: { width: '100%' },
    tabContent: { pt: 3 },
};

const AppointmentRequest = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    const { setTotalReqCount } = useGlobalStore();

    // ── Pagination & sorting ──────────────────────────────────────────────
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('dropOffDateTime');
    const [sortType, setSortType] = useState('DSC');
    const [dialogLoader, setDialogLoader] = useState(false);

    // ── Filters ───────────────────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [selectDate, setSelectDate] = useState(null);
    const [dateData, setDateData] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [vehicleOpen, setVehicleOpen] = useState(false);
    const [yearData, setYearData] = useState('');
    const [makeData, setMakeData] = useState('');
    const [modelData, setModelData] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [fleetUnitNumber, setFleetUnitNumber] = useState('');
    const [provinceId, setProvinceId] = useState('');
    const [vinFilter, setVinFilter] = useState('');

    // ── Tabs ──────────────────────────────────────────────────────────────
    const [tabValue, setTabValue] = useState(0);

    const [selectedRow, setSelectedRow] = useState(null);

    // ── Modals ────────────────────────────────────────────────────────────
    const [isOutstandingModalOpen, setIsOutstandingModalOpen] = useState(false);
    const [isUpcomingModal, setIsUpcomingModal] = useState(false);
    const [upcomingData, setUpcomingData] = useState({});
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
    const [appointmentModalData, setAppointmentModalData] = useState('');

    // ── Build params ───────────────────────────────────────────────────────
    const buildParams = useCallback(
        () => ({
            page,
            limit: rowsPerPage,
            tab: TAB_STATUS_MAP[tabValue] || 'outstanding',
            sortBy,
            sortType,
            search,
            date: dateData,
            status: selectedStatuses,
            year: yearData,
            make: makeData,
            model: modelData,
            licenseNumber,
            fleetUnitNumber,
            provinceId,
            vin: vinFilter,
        }),
        [
            page,
            rowsPerPage,
            tabValue,
            sortBy,
            sortType,
            search,
            dateData,
            selectedStatuses,
            yearData,
            makeData,
            modelData,
            licenseNumber,
            fleetUnitNumber,
            provinceId,
            vinFilter,
        ]
    );

    const queryParams = useMemo(() => buildParams(), [buildParams]);

    // Strip empty values before sending to API
    const cleanParams = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(queryParams).filter(([, v]) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
            ),
        [queryParams]
    );

    // ── TanStack Queries ───────────────────────────────────────────────────
    const { data: appointmentResponse, isLoading } = useAppointmentRequests(cleanParams, { enabled: true });
    const { data: cardCounts } = useAppointmentRequestsCard({ enabled: true });
    const { data: appointmentDetailData, isLoading: isDetailLoading } = useAppointmentDetails(
        selectedRow?.status !== APPOINTMENT_STATUS.CUSTOMER_PAID ? selectedRow?._id : null
    );

    const appointmentData = appointmentResponse?.data || [];
    const totalCount = appointmentResponse?.totalCount || 0;
    const showLoader = isLoading;

    const cancellationBannerType = useMemo(() => {
        if (tabValue !== 1 || !isOutstandingModalOpen) return null;
        const status = appointmentModalData?.requestStatus ?? selectedRow?.requestStatus;
        if (status === REQUEST_CANCEL_STATUS.SHOP) return 'shop';
        if (status === REQUEST_CANCEL_STATUS.CUSTOMER) return 'customer';
        return null;
    }, [tabValue, isOutstandingModalOpen, appointmentModalData?.requestStatus, selectedRow?.requestStatus]);

    const declineBannerType = useMemo(() => {
        if (tabValue !== 3) return null;
        const status = appointmentModalData?.requestStatus ?? selectedRow?.requestStatus;
        if (status === REQUEST_DECLINE_STATUS.SHOP) return 'shop';
        if (status === REQUEST_DECLINE_STATUS.CUSTOMER) return 'customer';
        return null;
    }, [tabValue, isOutstandingModalOpen, appointmentModalData?.requestStatus, selectedRow?.requestStatus]);
    // ── Redux helper (upcoming detail) ────────────────────────────────────
    const fetchUpcomingAppointmentDetails = async (id) => {
        if (!id) return;
        try {
            setDialogLoader(true);
            const detailView = await dispatch(appointmentDetails(id));
            if (detailView) setUpcomingData(detailView);
        } catch (error) {
            console.error('Error fetching upcoming appointment details:', error);
        } finally {
            setDialogLoader(false);
        }
    };

    const refreshAppointmentDetails = async (id) => {
        if (!id) return;
        setDialogLoader(true);
        try {
            await queryClient.invalidateQueries({ queryKey: appointmentKeys.details(id) });
        } finally {
            setDialogLoader(false);
        }
    };

    // ── Sync tab with URL ──────────────────────────────────────────────────
    useEffect(() => {
        const path = location.pathname.split('/');
        const routeName = path[path.length - 1];
        const tabIndex = ROUTE_TAB_MAP[routeName] ?? 0;
        setTabValue(tabIndex);
        setPage(1);
    }, [location.pathname]);

    // ── Update badge count ─────────────────────────────────────────────────
    useEffect(() => {
        if (tabValue === 0 && !showLoader) {
            setTotalReqCount(appointmentData?.length || 0);
        }
    }, [appointmentData]);

    // ── Sync detail data into modal state ─────────────────────────────────
    useEffect(() => {
        if (appointmentDetailData?.data?._id) {
            setAppointmentModalData(appointmentDetailData.data);
        }
    }, [appointmentDetailData]);

    // ── Handlers ───────────────────────────────────────────────────────────
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPage(1);
        setSelectedStatuses([]);
        navigate(TAB_ROUTES[newValue]);
    };

    const handleChangePage = (event, newPage) => setPage(newPage + 1);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleSort = (sortBy) => {
        setSortBy(sortBy);
        setSortType((prev) => (prev === 'DSC' ? 'ASC' : 'DSC'));
        setPage(1);
    };

    const handleDateChange = (date) => {
        setPage(1);
        if (!date) {
            setSelectDate(null);
            setDateData('');
            return;
        }
        const parsed = dayjs(date);
        if (!parsed.isValid()) return;
        setSelectDate(parsed);
        setDateData(parsed.format('YYYY-MM-DD'));
    };

    const debounceSearch = useMemo(
        () =>
            debounce((val) => {
                setSearch(val);
                setPage(1);
            }, 800),
        []
    );

    const handleSearchChange = (val) => {
        setSearchInput(val);
        debounceSearch(val);
    };

    // ── Modal close handlers ──────────────────────────────────────────────
    const handleCloseUpcomingModal = () => {
        setIsUpcomingModal(false);
        setUpcomingData({});
        queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: appointmentKeys.requestsCard(), refetchType: 'all' });
    };

    const handleCloseTodayModal = () => {
        setIsAppointmentModalOpen(false);
        setAppointmentModalData('');
        setSelectedRow(null);
        queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: appointmentKeys.requestsCard(), refetchType: 'all' });
    };

    const handleCloseRequestModal = () => {
        setIsOutstandingModalOpen(false);
        queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: appointmentKeys.requestsCard(), refetchType: 'all' });
    };

    // ── Clear filters ──────────────────────────────────────────────────────
    const hasClearableFilters =
        Boolean(dateData) ||
        (selectedStatuses?.length ?? 0) > 0 ||
        Boolean(search?.trim()) ||
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId);

    const clearFilterGroups = [
        Boolean(dateData),
        (selectedStatuses?.length ?? 0) > 0,
        Boolean(search?.trim()),
        Boolean(yearData || makeData || modelData || licenseNumber?.trim?.() || fleetUnitNumber?.trim?.() || vinFilter?.trim?.() || provinceId),
    ].filter(Boolean).length;

    const clearFilterLabel = clearFilterGroups > 1 ? 'Clear Filters' : 'Clear Filter';

    const handleClearFilters = () => {
        setSelectDate(null);
        setDateData('');
        setSelectedStatuses([]);
        setYearData('');
        setMakeData('');
        setModelData('');
        setLicenseNumber('');
        setFleetUnitNumber('');
        setProvinceId('');
        setVinFilter('');
        setSearch('');
        setSearchInput('');
        setPage(1);
    };

    // ── Row navigation ─────────────────────────────────────────────────────
    const handleView = (row) => {
        if (tabValue === 0) {
            setSelectedRow(row);
            setIsOutstandingModalOpen(true);
        } else if (tabValue === 1 && (row?.requestStatus === REQUEST_CANCEL_STATUS.SHOP || row?.requestStatus === REQUEST_CANCEL_STATUS.CUSTOMER)) {
            setSelectedRow(row);
            setIsOutstandingModalOpen(true);
        } else if ((tabValue === 1 && row?.appointmentType === 'past') || row?.status === APPOINTMENT_STATUS.CUSTOMER_PAID) {
            navigate(`/appointment/past-appointment/${row._id}`);
        } else if (tabValue === 1 && row?.appointmentType === 'upcoming' && row?.status !== APPOINTMENT_STATUS.CUSTOMER_PAID) {
            setIsUpcomingModal(true);
            fetchUpcomingAppointmentDetails(row._id);
        } else if (tabValue === 1 && row?.appointmentType === 'today' && row?.status !== APPOINTMENT_STATUS.CUSTOMER_PAID) {
            setIsAppointmentModalOpen(true);
            setSelectedRow(row);
        } else if (tabValue === 2) {
            setSelectedRow(row);
            setIsOutstandingModalOpen(true);
        } else if (tabValue === 3 || tabValue === 4) {
            setSelectedRow(row);
            setIsOutstandingModalOpen(true);
        }
    };

    const handleViewCustomer = (row) => navigate(`/customers/${row?.customer?._id}/${row?.customer?.createdFrom}`);

    // ─────────────────────────────────────────────────────────────────────
    return (
        <>
            <Box padding={'32px 24px'} sx={styles.pageBox}>
                <Grid container rowGap={3}>
                    <AppointmentRequestHeader onNewAppointment={() => navigate('/appointment/new-appointment')} />

                    <AppointmentRequestCards counts={cardCounts} />

                    <Grid container item>
                        <Box sx={styles.fullWidth}>
                            <AppointmentRequestTabs value={tabValue} onChange={handleTabChange} />

                            <Box sx={styles.tabContent}>
                                <Grid container item>
                                    {/* Hide filters when there are no results and no active filters */}
                                    {!(!hasClearableFilters && appointmentData?.length === 0 && !showLoader) && (
                                        <AppointmentRequestFilters
                                            tabValue={tabValue}
                                            selectDate={selectDate}
                                            onDateChange={handleDateChange}
                                            selectedStatuses={selectedStatuses}
                                            onStatusChange={(v) => setSelectedStatuses(v)}
                                            vehicleOpen={vehicleOpen}
                                            onVehicleOpen={() => setVehicleOpen(true)}
                                            onVehicleCancel={() => setVehicleOpen(false)}
                                            onVehicleApply={(v) => {
                                                setYearData(v.yearData || '');
                                                setMakeData(v.makeData || '');
                                                setModelData(v.modelData || '');
                                                setLicenseNumber(v.licenseNumber || '');
                                                setFleetUnitNumber(v.fleetUnitNumber || '');
                                                setProvinceId(v.provinceId || '');
                                                setVinFilter(v.vinFilter || '');
                                                setPage(1);
                                                setVehicleOpen(false);
                                            }}
                                            yearData={yearData}
                                            makeData={makeData}
                                            modelData={modelData}
                                            licenseNumber={licenseNumber}
                                            fleetUnitNumber={fleetUnitNumber}
                                            provinceId={provinceId}
                                            vinFilter={vinFilter}
                                            search={searchInput}
                                            onSearchChange={handleSearchChange}
                                            hasClearableFilters={hasClearableFilters}
                                            clearFilterLabel={clearFilterLabel}
                                            onClearFilters={handleClearFilters}
                                        />
                                    )}

                                    <AppointmentRequestTable
                                        filterApplied={hasClearableFilters}
                                        tabValue={tabValue}
                                        rows={appointmentData}
                                        showLoader={showLoader}
                                        totalCount={totalCount}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        sortBy={sortBy}
                                        sortType={sortType}
                                        onSort={handleSort}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        onView={handleView}
                                        onViewCustomer={handleViewCustomer}
                                        onRowClick={handleView}
                                    />
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* ── Upcoming Appointments modal ── */}
            <ViewUpcomingModal
                open={isUpcomingModal}
                handleClose={handleCloseUpcomingModal}
                upcomingData={upcomingData}
                fetchAppointmentDetails={fetchUpcomingAppointmentDetails}
                handleCancelModel={handleCloseUpcomingModal}
                recallDetailsAPI={fetchUpcomingAppointmentDetails}
            />

            {/* ── Today Appointment modal ── */}
            <AppointmentModel
                open={isAppointmentModalOpen}
                handleClose={handleCloseTodayModal}
                data={appointmentModalData}
                status={selectedRow?.status}
                recallDetailsAPI={refreshAppointmentDetails}
                fetchAppointmentDetails={refreshAppointmentDetails}
                setAppointmentDropOffODo={() => {}}
                setAppointmentCompletionOffODo={() => {}}
                setUpdateStatus={() => {
                    queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
                    queryClient.invalidateQueries({ queryKey: appointmentKeys.requestsCard(), refetchType: 'all' });
                }}
            />

            {/* ── Outstanding / Proposed / Declined / Expired modal ── */}
            <AppointmentRequestModal
                isProposeTime={tabValue === 0 || tabValue === 4}
                showNew={tabValue === 2}
                open={isOutstandingModalOpen}
                handleClose={handleCloseRequestModal}
                appointmentData={appointmentModalData}
                id={selectedRow?._id}
                handleCancelAppointment={handleCloseRequestModal}
                apiCall={async () => {
                    await queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
                }}
                showAcceptBtn={tabValue === 0}
                showDeclineBtn={tabValue === 0}
                showProposeBtn={tabValue === 0 || tabValue === 4}
                tab={tabValue}
                cancellationBannerType={cancellationBannerType}
                declineBannerType={declineBannerType}
            />

            {/* ── Loading overlay ── */}
            <Dialog open={showLoader || dialogLoader || isDetailLoading}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AppointmentRequest;
